/**
 * Componente para calcular frete usando a API do Shopify
 * Específico para CEPs do Brasil
 */
class ShippingCalculatorComponent extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.form = this.querySelector('form');
    this.zipInput = this.querySelector('#shipping-calculator-zip');
    this.submitButton = this.querySelector('#shipping-calculator-submit');
    this.resultsContainer = this.querySelector('#shipping-calculator-results');
    this.loadingIndicator = this.querySelector('#shipping-calculator-loading');
    this.errorMessage = this.querySelector('#shipping-calculator-error');
    this.toggleButton = this.querySelector('.shipping-calculator__toggle');
    this.content = this.querySelector('#shipping-calculator-content');
    
    // Read settings
    this.freeAll = this.getAttribute('data-free-all') === 'true';
    this.freeCheapest = this.getAttribute('data-free-cheapest') === 'true';
    this.threshold = parseInt(this.getAttribute('data-threshold') || '0', 10);
    this.freeMethodName = this.getAttribute('data-free-method-name') || '';
    this.cartTotal = parseInt(this.getAttribute('data-cart-total') || '0', 10);

    this.isExpanded = false;

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    if (this.zipInput) {
      this.zipInput.addEventListener('input', this.handleZipInput.bind(this));
    }

    if (this.toggleButton) {
      this.toggleButton.addEventListener('click', this.handleToggle.bind(this));
    }
  }

  handleZipInput(event) {
    const zip = event.target.value;
    // Formatar CEP brasileiro automaticamente
    const formattedZip = this.formatBrazilianZip(zip);
    if (formattedZip !== zip) {
      event.target.value = formattedZip;
    }

    // Validar CEP brasileiro
    const isValid = this.validateBrazilianZip(formattedZip);
    this.submitButton.disabled = !isValid;
    
    if (this.errorMessage) {
      this.errorMessage.style.display = isValid ? 'none' : 'block';
      this.errorMessage.textContent = isValid ? '' : this.getTranslation('shipping.error_invalid_zip', 'CEP inválido. Digite um CEP brasileiro válido.');
    }
  }

  formatBrazilianZip(zip) {
    // Remove todos os caracteres não numéricos
    const numbers = zip.replace(/\D/g, '');
    
    // Limita a 8 dígitos
    const limitedNumbers = numbers.substring(0, 8);
    
    // Adiciona a formatação do CEP brasileiro (XXXXX-XXX)
    if (limitedNumbers.length > 5) {
      return limitedNumbers.substring(0, 5) + '-' + limitedNumbers.substring(5);
    }
    
    return limitedNumbers;
  }

  validateBrazilianZip(zip) {
    // CEP brasileiro deve ter 8 dígitos no formato XXXXX-XXX
    const zipRegex = /^\d{5}-?\d{3}$/;
    return zipRegex.test(zip.replace('-', ''));
  }

  async handleSubmit(event) {
    event.preventDefault();
    
    const zip = this.zipInput.value.replace(/\D/g, ''); // Remove formatação
    
    if (!this.validateBrazilianZip(zip)) {
      this.showError(this.getTranslation('shipping.error_invalid_zip', 'CEP inválido. Digite um CEP brasileiro válido.'));
      return;
    }

    this.showLoading(true);
    this.clearResults();
    this.clearError();

    try {
      const shippingRates = await this.fetchShippingRates(zip);
      this.displayResults(shippingRates);
    } catch (error) {
      console.error('Erro ao calcular frete:', error);
      this.showError(this.getTranslation('shipping.error_calculation', 'Erro ao calcular frete. Tente novamente.'));
    } finally {
      this.showLoading(false);
    }
  }

  async fetchShippingRates(zip) {
    // Primeiro, buscar a província (estado) a partir do CEP usando a API ViaCEP
    const viaCepResponse = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
    if (!viaCepResponse.ok) {
      throw new Error('Não foi possível buscar a província pelo CEP.');
    }
    const viaCepData = await viaCepResponse.json();

    if (viaCepData.erro || !viaCepData.uf) {
      throw new Error('CEP inválido ou não encontrado.');
    }

    const province = viaCepData.uf; // Exemplo: 'SP', 'RJ', etc.

    // Agora, usar a API de shipping rates do Shopify com a província obrigatória
    const response = await fetch('/cart/shipping_rates.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        shipping_address: {
          zip: zip,
          country: 'BR',
          province: province
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Erro HTTP: ${response.status}`);
    }

    const data = await response.json();
    return data.shipping_rates || [];
  }

  displayResults(shippingRates) {
    if (!this.resultsContainer) return;

    this.resultsContainer.innerHTML = '';

    if (shippingRates.length === 0) {
      this.resultsContainer.innerHTML = `
        <div class="shipping-calculator__no-results">
          <p>${this.getTranslation('shipping.no_options', 'Nenhuma opção de entrega disponível para este CEP.')}</p>
        </div>
      `;
      return;
    }

    let rates = [...shippingRates];

    // Logic to modify prices based on settings
    if (this.freeAll) {
      rates = rates.map(rate => ({ ...rate, price: '0.00' }));
    } else if (this.freeCheapest && this.cartTotal >= this.threshold) {
      if (this.freeMethodName && this.freeMethodName.trim() !== '') {
         // Match by specific name (case insensitive)
         const targetName = this.freeMethodName.trim().toLowerCase();
         rates = rates.map(rate => {
           if (rate.name.toLowerCase().includes(targetName)) {
             return { ...rate, price: '0.00' };
           }
           return rate;
         });
      } else {
        // Find cheapest price
        const prices = rates.map(r => parseFloat(r.price));
        const minPrice = Math.min(...prices);
        
        // Make all rates with minPrice free
        rates = rates.map(rate => {
          if (parseFloat(rate.price) === minPrice) {
            return { ...rate, price: '0.00' };
          }
          return rate;
        });
      }
    }

    const resultsHTML = rates.map(rate => {
      const price = parseFloat(rate.price) === 0 ? this.getTranslation('shipping.free_shipping', 'Grátis') : `R$ ${parseFloat(rate.price).toFixed(2).replace('.', ',')}`;
      const deliveryDate = this.calculateDeliveryDate(rate.delivery_days);
      
      return `
        <div class="shipping-calculator__option">
          <div class="shipping-calculator__option-info">
            <div class="shipping-calculator__option-name">${rate.name}</div>
            <div class="shipping-calculator__option-price">
            ${parseFloat(rate.price) === 0 ? `<strong class="shipping-calculator__option-price-free">${price}</strong>` : price}
            </div>
          </div>
          ${deliveryDate ? `<div class="shipping-calculator__option-delivery">Entrega em até ${deliveryDate}</div>` : ''}
        </div>
      `;
    }).join('');

    this.resultsContainer.innerHTML = `
      <div class="shipping-calculator__results-header">
        <h4>${this.getTranslation('shipping.options_title', 'Opções de entrega:')}</h4>
      </div>
      <div class="shipping-calculator__options">
        ${resultsHTML}
      </div>
    `;
  }

  calculateDeliveryDate(deliveryDays) {
    if (!deliveryDays) return null;
    
    const days = parseInt(deliveryDays);
    if (isNaN(days)) return null;
    
    if (days === 1) return '1 dia útil';
    return `${days} dias úteis`;
  }

  showLoading(show) {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = show ? 'block' : 'none';
    }
    if (this.submitButton) {
      this.submitButton.disabled = show;
      this.submitButton.textContent = show ? this.getTranslation('shipping.calculating', 'Calculando...') : this.getTranslation('shipping.calculate_button', 'Calcular frete');
    }
  }

  clearResults() {
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = '';
    }
  }

  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.style.display = 'block';
    }
  }

  clearError() {
    if (this.errorMessage) {
      this.errorMessage.style.display = 'none';
      this.errorMessage.textContent = '';
    }
  }

  /**
   * Obtém tradução do Shopify ou usa fallback
   * @param {string} key - Chave da tradução
   * @param {string} fallback - Texto padrão se a tradução não for encontrada
   * @returns {string}
   */
  getTranslation(key, fallback) {
    try {
      // Tenta acessar as traduções do Shopify se disponíveis
      if (window.theme && window.theme.translations && window.theme.translations[key]) {
        return window.theme.translations[key];
      }
      
      // Se não houver traduções disponíveis, usa o fallback
      return fallback;
    } catch (error) {
      return fallback;
    }
  }

  /**
   * Gerencia o toggle do calculador (abrir/fechar)
   */
  handleToggle() {
    console.log('handleToggle');
    
    this.isExpanded = !this.isExpanded;
    
    if (this.content) {
      this.content.style.display = this.isExpanded ? 'block' : 'none';
    }
    
    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-expanded', this.isExpanded.toString());
    }
    
    // Adiciona/remove atributo data para CSS
    if (this.isExpanded) {
      this.setAttribute('data-expanded', 'true');
    } else {
      this.removeAttribute('data-expanded');
    }

    // Se está abrindo e o input está vazio, foca no campo de CEP
    if (this.isExpanded && this.zipInput && !this.zipInput.value.trim()) {
      setTimeout(() => {
        this.zipInput.focus();
      }, 100);
    }
  }
}

if (!customElements.get('shipping-calculator')) {
  customElements.define('shipping-calculator', ShippingCalculatorComponent);
}
